/**
 * Iter-185.BM (AUDIT #2) — Field-level drop-off telemetry.
 *
 * Audit problem (`/app/memory/AUDIT_2026-02.md` TIER 1 #2):
 *   "We have `event=quote_form_submit` but no field-level abandonment
 *    tracking. We can't see WHICH field kills the conversion (ZIP?
 *    credit slider? phone?)."
 *
 * Audit solution:
 *   - Add `field_blur` events to QuoteForm + every intake step
 *   - Heatmap-style /admin/funnel/drop-off dashboard per category
 *
 * This helper persists field-level events to the server-side
 * `funnel_field_events` collection via POST /api/funnel/field-event.
 * Fire-and-forget — failure (offline, blocked, network error) silently
 * swallows so it never blocks UI.
 *
 * Session ID: each browser session gets a UUID-like nonce in
 * sessionStorage. Multiple fires from the same session count as ONE
 * for the drop-off aggregator (avoids inflation from spammy refreshers).
 */
import { api } from "./api";

const SESSION_KEY = "carsavr_funnel_session_id";

function getSessionId() {
  if (typeof window === "undefined") return "ssr";
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      // Crypto-strong nonce, prefixed for log readability.
      id =
        "fs_" +
        Math.random().toString(36).slice(2, 10) +
        Date.now().toString(36);
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "private";
  }
}

/**
 * Record a field-level funnel event.
 *
 * @param {object} args
 * @param {string} args.surface     — surface identifier ("intake_insurance" etc.)
 * @param {string} args.category    — funnel category
 * @param {string} args.event_type  — "focus" | "blur" | "advance" | "submit"
 * @param {string} args.field       — field name (or "step_advance" / "form_submit")
 * @param {number} args.step        — 0-based step index
 * @param {boolean} args.completed  — true if the blur event captured a value
 */
export function recordFunnelFieldEvent({
  surface,
  category = "unknown",
  event_type,
  field,
  step = 0,
  completed = false,
}) {
  if (!surface || !event_type || !field) return;
  try {
    api
      .post("/funnel/field-event", {
        surface,
        category,
        event_type,
        field,
        step,
        completed: Boolean(completed),
        session_id: getSessionId(),
      })
      .catch(() => {});
  } catch {
    /* swallow */
  }
}

/**
 * Convenience helper for blur events. Bundles in the `completed` flag
 * so the dashboard can distinguish "user tabbed past empty" vs.
 * "user typed a value".
 */
export function recordFieldBlur({ surface, category, field, step, value }) {
  recordFunnelFieldEvent({
    surface,
    category,
    event_type: "blur",
    field,
    step,
    completed: Boolean(value && String(value).trim().length > 0),
  });
}

/** Convenience: focus event (entered the field). */
export function recordFieldFocus({ surface, category, field, step }) {
  recordFunnelFieldEvent({
    surface,
    category,
    event_type: "focus",
    field,
    step,
  });
}

/** Convenience: step advance. */
export function recordStepAdvance({ surface, category, from_step, to_step }) {
  recordFunnelFieldEvent({
    surface,
    category,
    event_type: "advance",
    field: "step_advance",
    step: to_step,
    completed: true,
  });
  // also persist the from_step for completeness (one extra event so
  // the aggregator can render "step N exit" vs "step N+1 entry")
  recordFunnelFieldEvent({
    surface,
    category,
    event_type: "advance",
    field: `step_${from_step}_exit`,
    step: from_step,
    completed: true,
  });
}

/** Convenience: form submit attempt. */
export function recordFormSubmit({ surface, category, step }) {
  recordFunnelFieldEvent({
    surface,
    category,
    event_type: "submit",
    field: "form_submit",
    step,
    completed: true,
  });
}
